import { injectStyle } from "../core/style";
import {
  attr,
  hasExplicitNumericHeight,
  resolveHeight,
  resolveWidth,
  usesAutoHeight,
} from "../core/dom";
import { icon } from "../core/icons";

// layout primitives: component styles, assembled into the global runtime
// stylesheet by core/style.ts. References design tokens via var(--rp-*).
export const layoutStyle = `
viewport-el, viewport-el { display:flex; flex-direction:column; width:var(--snap-width,1440px); height:var(--snap-height,900px); background:var(--rp-bg); overflow:hidden; color:var(--rp-c-gray-900); }
viewport-el[device="ui-slice"], viewport-el[device="ui-slice"] { border:1px solid var(--rp-c-zinc-200); border-radius:var(--rp-radius-lg); background:var(--rp-surface); padding:20px; }
layout-el, layout-el { display:grid; grid-template-columns:var(--snap-columns,1fr); grid-template-rows:var(--snap-rows,auto); gap:var(--snap-gap,0); align-content:start; width:100%; max-width:100%; min-width:0; }
layout-el[align="center"], layout-el[align="center"] { align-items:center; }
layout-el[align="start"], layout-el[align="start"] { align-items:start; }
layout-el[align="end"], layout-el[align="end"] { align-items:end; }
layout-el[justify="center"], layout-el[justify="center"] { justify-items:center; }
layout-el[justify="end"], layout-el[justify="end"] { justify-items:end; }
panel-el, panel-el { margin:0; display:block; width:100%; max-width:100%; background:var(--rp-surface); border:1px solid var(--rp-border); border-radius:var(--rp-radius-md); padding:12px; }
panel-el[elevation="1"], panel-el[elevation="1"] { box-shadow:0 4px 16px var(--rp-a-slate-06); }
panel-el[elevation="2"], panel-el[elevation="2"] { box-shadow:var(--rp-shadow); }
navbar-el, navbar-el { display:flex; align-items:center; gap:10px; width:100%; max-width:100%; height:var(--snap-height,48px); padding:0 12px; background:var(--rp-surface); border-bottom:1px solid var(--rp-border); }
sidebar-el, sidebar-el { display:block; width:var(--snap-width,260px); min-height:0; background:var(--rp-surface); border-right:1px solid var(--rp-border); padding:8px; }
sidebar-el[collapsed], sidebar-el[collapsed] { width:72px; }
logo-el, logo-el { display:inline-grid; place-items:center; width:var(--snap-size,82px); height:32px; border-radius:var(--rp-radius-md); background:var(--rp-c-gray-900); color:var(--rp-c-white); font-size:12px; font-weight:800; letter-spacing:.08em; }
split-pane, split-pane { display:grid; grid-template-columns:var(--snap-columns,1fr 1fr); width:100%; max-width:100%; border:1px solid var(--rp-border); border-radius:var(--rp-radius-md); overflow:hidden; }
divider-el, divider-el { display:block; height:1px; background:var(--rp-border); margin:12px 0; }
divider-el.divider-el-v, divider-el.divider-el-v { display:inline-block; width:1px; height:auto; align-self:stretch; margin:0 12px; }
spacer-el, spacer-el { display:block; height:var(--snap-size,16px); }
row-el, row-el { display:flex; flex-direction:row; gap:var(--snap-gap,0px); align-items:var(--snap-align,stretch); width:100%; max-width:100%; }
row-el > *, row-el > * { min-width:0; flex-shrink:1; }
col-el, col-el { display:flex; flex-direction:column; gap:var(--snap-gap,0px); flex:1; min-width:0; }
section-el, section-el { display:block; padding:0; }
layout-el > *, layout-el > * { min-width:0; }

/* separator — visual divider with horizontal/vertical orientation */
separator-el, separator-el { display:block; height:1px; width:100%; background:var(--rp-border); margin:12px 0; border:0; }
separator-el[orientation="vertical"], separator-el[orientation="vertical"] { display:inline-block; width:1px; height:auto; align-self:stretch; margin:0 12px; }

/* icon — placeholder icon display with mode (line/fill) and type */
icon-el, icon-el { display:inline-flex; align-items:center; justify-content:center; width:var(--snap-size,32px); height:var(--snap-size,32px); color:var(--rp-c-gray-500); }
icon-el[mode="fill"], icon-el[mode="fill"] { background:var(--rp-c-gray-100); border-radius:var(--rp-radius-md); }
icon-el[color="success"], icon-el[color="success"] { color:var(--rp-success); }
icon-el[color="danger"], icon-el[color="danger"] { color:var(--rp-danger); }
icon-el[color="warning"], icon-el[color="warning"] { color:var(--rp-warning); }
icon-el[color="primary"], icon-el[color="primary"] { color:var(--rp-primary); }
icon-el[color="muted"], icon-el[color="muted"] { color:var(--rp-muted); }
icon-el[color="white"], icon-el[color="white"] { color:var(--rp-c-white); }

/* app-shell — sidebar + topbar + main content layout */
app-shell, app-shell { display:flex; width:100%; max-width:100%; min-width:0; height:var(--snap-height,480px); background:var(--rp-bg); border:1px solid var(--rp-border); border-radius:var(--rp-radius-md); overflow:hidden; }
app-shell[direction="row-reverse"], app-shell[direction="row-reverse"] { flex-direction:row-reverse; }
.app-shell-sidebar { flex:0 0 auto; }
.app-shell-main { display:flex; flex-direction:column; flex:1 1 auto; min-width:0; }
.app-shell-topbar { flex:0 0 auto; }
.app-shell-content { flex:1 1 auto; overflow:auto; padding:0; }

/* toolbar — horizontal action bar */
toolbar, toolbar { display:flex; align-items:center; gap:8px; width:100%; max-width:100%; min-width:0; padding:8px 12px; background:var(--rp-surface); border:1px solid var(--rp-border); border-radius:var(--rp-radius-md); }
toolbar > * { flex-shrink:1; }
toolbar > search, toolbar > input { flex:1 1 auto; }

/* flex-layout — flexbox-based layout primitive */
flex-layout, flex-layout { display:flex; flex-direction:var(--snap-direction,row); gap:var(--snap-gap,0px); align-items:var(--snap-align,stretch); justify-content:var(--snap-justify,flex-start); flex-wrap:var(--snap-wrap,nowrap); width:100%; max-width:100%; min-width:0; padding:0; }
flex-layout > * { min-width:0; flex-shrink:1; flex-grow:0; flex-basis:auto; text-align:start; }
flex-layout > flex-layout { width:auto; }
flex-layout > heading-el, flex-layout > text-el { align-self:stretch; }
flex-layout[direction="column"], flex-layout[direction="column"] { flex-direction:column; }
flex-layout[direction="row-reverse"], flex-layout[direction="row-reverse"] { flex-direction:row-reverse; }
flex-layout[direction="column-reverse"], flex-layout[direction="column-reverse"] { flex-direction:column-reverse; }
flex-layout[align="start"], flex-layout[align="start"] { align-items:flex-start; }
flex-layout[align="center"], flex-layout[align="center"] { align-items:center; }
flex-layout[align="end"], flex-layout[align="end"] { align-items:flex-end; }
flex-layout[justify="start"], flex-layout[justify="start"] { justify-content:flex-start; }
flex-layout[justify="center"], flex-layout[justify="center"] { justify-content:center; }
flex-layout[justify="end"], flex-layout[justify="end"] { justify-content:flex-end; }
flex-layout[justify="between"], flex-layout[justify="between"] { justify-content:space-between; }
flex-layout[justify="around"], flex-layout[justify="around"] { justify-content:space-around; }
flex-layout[wrap], flex-layout[wrap] { flex-wrap:wrap; }
flex-layout[divider] > * + * { border-top:1px solid var(--rp-border); }
flex-layout[bg="muted"] { background:var(--rp-c-zinc-50); }
flex-layout[highlight] { background:var(--rp-c-blue-50); border-left:3px solid var(--rp-primary); border-radius:0 var(--rp-radius-sm) var(--rp-radius-sm) 0; }
flex-layout[bordered] { border:1px solid var(--rp-border); border-radius:var(--rp-radius-md); }
flex-layout[py] { padding-top:var(--snap-py,0px); padding-bottom:var(--snap-py,0px); }
flex-layout[px] { padding-left:var(--snap-px,0px); padding-right:var(--snap-px,0px); }

/* flex-layout child flex control via attributes */
flex-layout > [flex="1"] { flex:1 1 0%; }
flex-layout > [flex="2"] { flex:2 1 0%; }
flex-layout > [flex="3"] { flex:3 1 0%; }
flex-layout > [flex="auto"] { flex:0 1 auto; }
flex-layout > [flex="grow"] { flex:1 1 auto; }
flex-layout > [flex="none"] { flex:0 0 auto; }
flex-layout > [flex="0"] { flex:0 0 auto; }
flex-layout > [flex-grow="1"] { flex-grow:1; }
flex-layout > [flex-grow="2"] { flex-grow:2; }
flex-layout > [flex-shrink="0"] { flex-shrink:0; }
flex-layout > [align-self="start"] { align-self:flex-start; }
flex-layout > [align-self="center"] { align-self:center; }
flex-layout > [align-self="end"] { align-self:flex-end; }
flex-layout > [align-self="stretch"] { align-self:stretch; }
flex-layout > [align-self="auto"] { align-self:auto; }
flex-layout > [order="1"] { order:1; }
flex-layout > [order="2"] { order:2; }
flex-layout > [order="3"] { order:3; }

/* heading — design-token-controlled heading (no doc-mode margins) */
heading-el, heading-el { display:block; margin:0; line-height:1.3; font-weight:700; color:var(--rp-text); letter-spacing:-.01em; word-break:break-word; overflow-wrap:break-word; text-align:start; }
heading-el[data-level="1"] { font-size:28px; font-weight:750; }
heading-el[data-level="2"] { font-size:22px; font-weight:720; }
heading-el[data-level="3"] { font-size:18px; font-weight:680; }
heading-el[data-level="4"] { font-size:16px; font-weight:650; }
heading-el[data-level="5"] { font-size:14px; font-weight:620; }
heading-el[data-level="6"] { font-size:13px; font-weight:600; color:var(--rp-muted); text-transform:uppercase; letter-spacing:.04em; }
heading-el[data-align="end"] { text-align:end; }
heading-el[data-align="center"] { text-align:center; }
heading-el[data-color="success"] { color:var(--rp-success); }
heading-el[data-color="danger"] { color:var(--rp-danger); }
heading-el[data-color="warning"] { color:var(--rp-warning); }
heading-el[data-color="primary"] { color:var(--rp-primary); }
heading-el[data-color="white"] { color:var(--rp-c-white); }

/* text — design-token-controlled text (no doc-mode margins) */
text-el, text-el { display:block; margin:0; line-height:1.5; font-size:14px; color:var(--rp-text); word-break:break-word; overflow-wrap:break-word; text-align:start; }
text-el[data-size="xs"] { font-size:11px; }
text-el[data-size="sm"] { font-size:12px; }
text-el[data-size="md"] { font-size:14px; }
text-el[data-size="lg"] { font-size:16px; }
text-el[data-size="xl"] { font-size:18px; }
text-el[data-variant="muted"] { color:var(--rp-muted); }
text-el[data-variant="secondary"] { color:var(--rp-c-gray-600); }
text-el[data-color="success"] { color:var(--rp-success); }
text-el[data-color="danger"] { color:var(--rp-danger); }
text-el[data-color="warning"] { color:var(--rp-warning); }
text-el[data-color="primary"] { color:var(--rp-primary); }
text-el[data-color="white"] { color:var(--rp-c-white); }
text-el[data-align="end"] { text-align:end; }
text-el[data-align="center"] { text-align:center; }
text-el[data-weight="medium"] { font-weight:500; }
text-el[data-weight="semibold"] { font-weight:600; }
text-el[data-weight="bold"] { font-weight:700; }
`;

export class GenericElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
  }
}
export class ViewportElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.hasAttribute("width") || this.hasAttribute("device"))
      this.style.setProperty("--snap-width", `${resolveWidth(this, 1440)}px`);
    if (hasExplicitNumericHeight(this))
      this.style.setProperty("--snap-height", `${resolveHeight(this, 900)}px`);
    else if (usesAutoHeight(this))
      this.style.setProperty("--snap-height", "auto");
  }
}
export class LayoutElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    this.style.setProperty("--snap-columns", attr(this, "columns", "1fr"));
    this.style.setProperty("--snap-rows", attr(this, "rows", "auto"));
    this.style.setProperty("--snap-gap", `${attr(this, "gap", "0")}px`);
  }
}
export class PanelElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.hasAttribute("padding"))
      this.style.padding = `${attr(this, "padding", "12")}px`;
  }
}
export class NavbarElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    this.style.setProperty("--snap-height", `${attr(this, "height", "48")}px`);
  }
}
export class SidebarElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    this.style.setProperty("--snap-width", `${attr(this, "width", "260")}px`);
  }
}
export class LogoElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.hasAttribute("size"))
      this.style.setProperty("--snap-size", `${attr(this, "size", "82")}px`);
    if (!this.innerHTML.trim()) this.textContent = attr(this, "label", "LOGO");
  }
}
export class SplitPaneElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    this.style.setProperty("--snap-columns", attr(this, "columns", "1fr 1fr"));
  }
}
export class DividerElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.hasAttribute("vertical")) this.classList.add("divider-el-v");
  }
}
export class SpacerElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.hasAttribute("size"))
      this.style.setProperty("--snap-size", `${attr(this, "size", "16")}px`);
  }
}
export class RowElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    this.style.setProperty("--snap-gap", `${attr(this, "gap", "0")}px`);
    this.style.setProperty("--snap-align", attr(this, "align", "stretch"));
  }
}
export class ColElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    this.style.setProperty("--snap-gap", `${attr(this, "gap", "0")}px`);
  }
}
export class SectionElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.hasAttribute("padding"))
      this.style.padding = `${attr(this, "padding", "0")}px`;
  }
}

export class SeparatorElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.hasAttribute("orientation"))
      this.setAttribute("orientation", attr(this, "orientation", "horizontal"));
  }
}

export class IconElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.dataset.rpReady) return;
    this.dataset.rpReady = "true";
    const size = attr(this, "size", "32");
    this.style.setProperty("--snap-size", `${size}px`);
    const type = attr(this, "type", "");
    const mode = attr(this, "mode", "line");
    this.setAttribute("mode", mode);
    if (type) {
      this.innerHTML = icon(type, Number(size));
    } else {
      this.innerHTML = icon("sparkles", Number(size));
    }
  }
}

export class AppShellElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.dataset.rpReady) return;
    this.dataset.rpReady = "true";
    if (this.hasAttribute("height"))
      this.style.setProperty(
        "--snap-height",
        `${attr(this, "height", "480")}px`,
      );
    const children = Array.from(this.childNodes);
    const sidebar = children.find(
      (n) => (n as HTMLElement).tagName === "SIDEBAR-EL",
    );
    const navigator = children.find(
      (n) => (n as HTMLElement).tagName === "NAVBAR-EL",
    );
    const content = children.filter((n) => n !== sidebar && n !== navigator);
    const main = document.createElement("div");
    main.className = "app-shell-main";
    if (navigator) {
      const topbar = document.createElement("div");
      topbar.className = "app-shell-topbar";
      topbar.appendChild(navigator);
      main.appendChild(topbar);
    }
    const contentDiv = document.createElement("div");
    contentDiv.className = "app-shell-content";
    if (this.hasAttribute("padding"))
      contentDiv.style.padding = `${attr(this, "padding", "0")}px`;
    content.forEach((n) => contentDiv.appendChild(n));
    main.appendChild(contentDiv);
    this.innerHTML = "";
    if (sidebar) {
      const sb = document.createElement("div");
      sb.className = "app-shell-sidebar";
      sb.appendChild(sidebar);
      this.appendChild(sb);
    }
    this.appendChild(main);
  }
}

export class ToolbarElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
  }
}

export class FlexLayoutElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    this.style.setProperty("--snap-direction", attr(this, "direction", "row"));
    this.style.setProperty("--snap-gap", `${attr(this, "gap", "0")}px`);
    this.style.setProperty("--snap-align", attr(this, "align", "stretch"));
    this.style.setProperty(
      "--snap-justify",
      attr(this, "justify", "flex-start"),
    );
    this.style.setProperty(
      "--snap-wrap",
      this.hasAttribute("wrap") ? "wrap" : "nowrap",
    );
    if (this.hasAttribute("padding"))
      this.style.padding = `${attr(this, "padding", "0")}px`;
    if (this.hasAttribute("py"))
      this.style.setProperty("--snap-py", `${attr(this, "py", "0")}px`);
    if (this.hasAttribute("px"))
      this.style.setProperty("--snap-px", `${attr(this, "px", "0")}px`);
    Array.from(this.children).forEach((child) => {
      const el = child as HTMLElement;
      if (el.hasAttribute("flex-basis"))
        el.style.flexBasis = attr(el, "flex-basis", "auto");
    });
  }
}

export class HeadingElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.dataset.rpReady) return;
    this.dataset.rpReady = "true";
    const level = attr(this, "level", "3");
    this.dataset.level = level;
    const variant = attr(this, "variant", "");
    if (variant) this.dataset.variant = variant;
    const color = attr(this, "color", "");
    if (color) this.dataset.color = color;
    const align = attr(this, "align", "");
    if (align) this.dataset.align = align;
    if (!this.textContent?.trim()) this.textContent = "Heading";
  }
}

export class TextElement extends HTMLElement {
  connectedCallback() {
    injectStyle();
    if (this.dataset.rpReady) return;
    this.dataset.rpReady = "true";
    const size = attr(this, "size", "md");
    this.dataset.size = size;
    const variant = attr(this, "variant", "");
    if (variant) this.dataset.variant = variant;
    const weight = attr(this, "weight", "");
    if (weight) this.dataset.weight = weight;
    const color = attr(this, "color", "");
    if (color) this.dataset.color = color;
    const align = attr(this, "align", "");
    if (align) this.dataset.align = align;
    if (!this.textContent?.trim()) this.textContent = "";
  }
}
